import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { galleryData as initialGalleryData } from '../data/galleryData';

const GalleryContainer = styled.div`
  padding-top: 120px;
  padding-bottom: 60px;
  padding-left: 20px;
  padding-right: 20px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Title = styled.h1`
  color: ${props => props.theme.colors.primary};
  text-align: center;
  margin-bottom: 35px;
  font-size: 2.8rem;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
  
  span {
    display: block;
    font-size: 1.2rem;
    color: ${props => props.theme.colors.secondary};
    margin-top: 15px;
    font-weight: normal;
    line-height: 1.6;
    word-break: keep-all;

    .mobile-break {
      display: none;
    }

    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      .mobile-break {
        display: inline;
      }
      font-size: 1.1rem;
      line-height: 1.4;
      padding: 0 10px;
      word-spacing: -1px;
    }
    
    @media (max-width: 480px) {
      font-size: 1.05rem;
      word-spacing: -1.5px;
      letter-spacing: -0.3px;
    }
    
    @media (max-width: 400px) {
      font-size: 1rem;
      word-spacing: -2px;
      letter-spacing: -0.5px;
    }
  }
`;

const Description = styled.p`
  text-align: center;
  color: ${props => props.theme.colors.text};
  margin-bottom: 80px;
  font-size: 1.15rem;
  line-height: 1.8;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  word-break: keep-all;
  padding: 0 20px;
  
  strong {
    color: ${props => props.theme.colors.primary};
    font-weight: 500;
  }

  .mobile-break {
    display: none;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    .mobile-break {
      display: inline;
    }
  }
`;

const GallerySection = styled.section`
  margin-bottom: 100px;
  padding: ${props => props.isTreatment ? '60px 30px' : '40px'};
  background: ${props => props.index % 2 === 0 ? props.theme.colors.pastel.mint : props.theme.colors.pastel.yellow};
  border-radius: ${props => props.theme.borderRadius.large};

  h2 {
    color: ${props => props.theme.colors.primary};
    font-size: 2.2rem;
    margin-bottom: ${props => props.isTreatment ? '60px' : '45px'};
    text-align: center;
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      bottom: -15px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 3px;
      background: ${props => props.theme.colors.secondary};
      border-radius: 2px;
    }
  }
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
  
  ${props => props.isTreatment && `
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    
    .gallery-item {
      aspect-ratio: 3/4;
    }
    
    @media (max-width: 1200px) {
      grid-template-columns: repeat(2, 1fr);
      gap: 25px;
    }
    
    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      grid-template-columns: repeat(2, 1fr);
      gap: 20px;
    }
    
    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      grid-template-columns: 1fr;
      gap: 25px;
    }
  `}
  
  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 25px;
  }
`;

const GalleryItem = styled(motion.div)`
  position: relative;
  border-radius: ${props => props.theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.medium};
  aspect-ratio: ${props => props.isVertical ? '3/4' : '16/9'};
  background: ${props => props.theme.colors.background};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      to bottom,
      rgba(77, 200, 196, 0),
      rgba(77, 200, 196, 0.3),
      rgba(77, 200, 196, 0.8)
    );
    display: flex;
    align-items: flex-end;
    padding: 25px;
    opacity: 0;
    transition: all 0.4s ease;

    p {
      color: white;
      font-size: 1.15rem;
      margin-bottom: 10px;
      transform: translateY(20px);
      transition: transform 0.4s ease;
      text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.15);
      font-weight: 500;
    }
  }

  &:hover {
    img {
      transform: scale(1.08);
    }

    .overlay {
      opacity: 1;
      
      p {
        transform: translateY(0);
      }
    }
  }
`;

const Modal = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
  cursor: pointer;
`;

const ModalContent = styled(motion.div)`
  max-width: 90vw;
  max-height: 90vh;
  position: relative;
  
  img {
    max-width: 100%;
    max-height: 90vh;
    object-fit: contain;
    border-radius: ${props => props.theme.borderRadius.medium};
  }
  
  button {
    position: absolute;
    top: -40px;
    right: 0;
    background: none;
    border: none;
    color: white;
    font-size: 24px;
    cursor: pointer;
    padding: 10px;
    
    &:hover {
      opacity: 0.8;
    }
  }
`;

function Gallery() {
  const [selectedImage, setSelectedImage] = useState(null);

  const getSectionTitle = (section) => {
    const isMobile = window.innerWidth <= 768; // tablet breakpoint

    switch (section) {
      case 'reception':
        return isMobile ? '대기실' : '접수실 & 대기실';
      case 'treatment':
        return '진료실';
      case 'consultation':
        return isMobile ? '상담실' : '상담실 & 기타 시설';
      default:
        return '';
    }
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'unset';
  };

  return (
    <GalleryContainer>
      <Title>
        치과 둘러보기
        <span>
          편안하고 안전한 진료환경을 위해<br className="mobile-break" /> 최선을 다하고 있습니다
        </span>
      </Title>
      
      <Description>
        서울쥬니어치과는 아이들이 편안하게 진료받을 수 있도록 밝고 따뜻한 분위기로 꾸며진 공간입니다.<span className="mobile-break"><br /></span>
      </Description>

      {Object.entries(initialGalleryData).map(([section, images], index) => (
        <GallerySection key={section} index={index} isTreatment={section === 'treatment'}>
          <h2>{getSectionTitle(section)}</h2>
          <GalleryGrid isTreatment={section === 'treatment'}>
            {images.map((image, imageIndex) => (
              <GalleryItem
                key={`${image.src}-${imageIndex}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: imageIndex * 0.1 }}
                isVertical={section === 'treatment'}
                onClick={() => handleImageClick(image)}
                whileHover={{ y: -5 }}
                className="gallery-item"
              >
                <img src={image.src} alt={image.alt} loading="lazy" />
                <div className="overlay">
                  <p>{image.description}</p>
                </div>
              </GalleryItem>
            ))}
          </GalleryGrid>
        </GallerySection>
      ))}

      <AnimatePresence>
        {selectedImage && (
          <Modal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseModal}
          >
            <ModalContent
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
            >
              <button onClick={handleCloseModal}>✕</button>
              <img
                src={selectedImage.src}
                alt={selectedImage.description}
              />
            </ModalContent>
          </Modal>
        )}
      </AnimatePresence>
    </GalleryContainer>
  );
}

export default Gallery; 